import './HvacPage.scss';
import { HvacArea, HvacGroup } from '@belimo-retrofit-portal/logic';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useAction } from 'src/hooks/useAction';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { BottomNavigationHelper } from 'src/modules/common/components/BottomNavigationHelper';
import { Breadcrumb } from 'src/modules/common/components/Breadcrumb';
import { BreadcrumbContainer } from 'src/modules/common/components/BreadcrumbContainer';
import { PageDescription } from 'src/modules/common/components/PageDescription';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { AppLayout } from 'src/modules/common/layouts/AppLayout';
import { FormError } from 'src/modules/form/types/FormError';
import { HVAC_CHANGE } from 'src/modules/hvac/actions/HvacActions';
import { getHvacConfig } from 'src/modules/hvac/selectors/getHvacConfig';
import { getHvacFormData } from 'src/modules/hvac/selectors/getHvacFormData';
import { getHvacSchemaGeneration } from 'src/modules/hvac/selectors/getHvacSchemaGeneration';
import { HvacFormData } from 'src/modules/hvac/types/HvacFormData';
import { HvacFormGroupView } from 'src/modules/hvac/views/HvacFormGroupView';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { ShareProject } from 'src/modules/sharing-projects/containers/ShareProject';

export const HvacGenerationPage = (): React.ReactElement => {
  const intl = useIntl();
  const project = useSelector(getProjectDetails);

  const config = useSelector(getHvacConfig);
  const schema = useParametricSelector(getHvacSchemaGeneration, project);
  const formData = useParametricSelector(getHvacFormData, project);
  const saveFormData = useAction(HVAC_CHANGE.trigger);

  // FIXME: split HVAC form into settings and groups
  const hvacForm = useForm<HvacFormData, FormError>('hvac', {
    reinitialize: false,
    initialValues: formData,
  });

  const areasField = hvacForm.field.at('assessment').at('areas');
  const heatingEnabled = true; // useFormWatch(hvacForm, areasField.at(HvacArea.HEATING).at('enabled').value.get);
  const coolingEnabled = true; // useFormWatch(hvacForm, areasField.at(HvacArea.COOLING).at('enabled').value.get);
  const ventilationEnabled = true; // useFormWatch(hvacForm, areasField.at(HvacArea.VENTILATION).at('enabled').value.get);
  const tabsDisabled = !heatingEnabled && !coolingEnabled && !ventilationEnabled;

  useEffect(() => hvacForm.subscribe((prevState) => {
    const nextState = hvacForm.currentState;
    if (prevState.values !== nextState.values) {
      saveFormData(nextState.values);
    }
  }), [hvacForm, saveFormData]);

  const navigation = (
    <BottomNavigationHelper
      routeBackText={intl.formatMessage({ id: 'common/bottomNavigation/hvac/generation/back' })}
      routeBackPath={`/project/${encodeURIComponent(project.id)}/hvac/distribution`}
      routeForthText={intl.formatMessage({ id: 'common/bottomNavigation/hvac/generation/next' })}
      routeForthPath={`/project/${encodeURIComponent(project.id)}/hvac/results`}
      nextRouteDisabled={tabsDisabled}
    />
  );

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({ id: 'hvac/pageTitle' })}
        </title>
      </Helmet>

      <AppLayout>

        <div className="bp-hvac-page">
          <div className="bp-hvac-page__content">
            <BreadcrumbContainer>
              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}`}
                title={intl.formatMessage({ id: 'project/toolbar/navigation/basedata' })}
                value={<FormattedMessage id="basedata/tabs/value" values={{ sub, sup }}/>}
                isEmpty={false}
                isFilled={true}
                disabled={false}
              />
              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/emission`}
                title={intl.formatMessage({ id: 'hvac/tabs/emission/title' })}
                value={<FormattedMessage id="hvac/tabs/emission/value" values={{ sub, sup }}/>}
                isEmpty={false}
                isFilled={true}
                disabled={tabsDisabled}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/distribution`}
                title={intl.formatMessage({ id: 'hvac/tabs/distribution/title' })}
                value={<FormattedMessage id="hvac/tabs/distribution/value" values={{ sub, sup }}/>}
                isEmpty={false}
                disabled={tabsDisabled}
                isFilled={true}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/generation`}
                title={intl.formatMessage({ id: 'hvac/tabs/generation/title' })}
                value={<FormattedMessage id="hvac/tabs/generation/value" values={{ sub, sup }}/>}
                isEmpty={false}
                disabled={tabsDisabled}
                isFilled={false}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/results`}
                title={intl.formatMessage({ id: 'hvac/tabs/results/title' })}
                value={<FormattedMessage id="hvac/tabs/results/value" values={{ sub, sup }}/>}
                isEmpty={true}
                disabled={tabsDisabled}
                isFilled={false}
              />
            </BreadcrumbContainer>

            {navigation}

            <PageDescription
              className="bp-hvac-page-description--container"
              title="hvac/generation/header"
              description="hvac/generation/description"
            />

            <div className="bp-hvac-page__group-list">
              <div className="bp-hvac-page__group-item">
                <HvacFormGroupView
                  group={HvacGroup.GENERATION}
                  form={hvacForm}
                  config={config}
                  schema={schema}
                  isAddingAllowed={true}
                  readOnly={project.readOnly}
                />
              </div>
            </div>

            {navigation}

          </div>
        </div>
      </AppLayout>

      <ShareProject/>
    </>
  );
};
